import React, { useState, useEffect, useContext } from 'react';
import { Button, Stack, FormControl, FormLabel } from '@mui/material';
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import { DateRange } from '@mui/x-date-pickers-pro/models';
import Modal from 'components/_layouts/Modal';
import { FilterAlt } from '@mui/icons-material';
import { translate as t } from 'hooks/useTranslations';
import JurisdictionFilter from 'views/App/Portal/DTB/_components/JurisdictionFilter';
import ManualPicker from 'components/_pickers/ManualPicker';
import { AppContext } from 'views/App/context';

export type AssignmentFilterModalProps = {
  open: boolean;
  onClose: () => void;
  filters: {
    jurisdictionId?: string;
    manualId?: string;
    createdAtStart?: string;
    createdAtEnd?: string;
  };
  onApply: (filters: AssignmentFilterModalProps['filters']) => void;
};

const AssignmentFilterModal: React.FC<AssignmentFilterModalProps> = ({ open, onClose, filters, onApply }) => {
  const { currentOrganizationId } = useContext(AppContext);
  const [jurisdiction, setJurisdiction] = useState<string>(filters.jurisdictionId || '');
  const [manualId, setManualId] = useState<string>(filters.manualId || '');
  const [createdDateRange, setCreatedDateRange] = useState<DateRange<any>>([
    filters.createdAtStart ? filters.createdAtStart : null,
    filters.createdAtEnd ? filters.createdAtEnd : null,
  ]);

  useEffect(() => {
    setJurisdiction(filters.jurisdictionId || '');
    setManualId(filters.manualId || '');
    setCreatedDateRange([
      filters.createdAtStart ? filters.createdAtStart : null,
      filters.createdAtEnd ? filters.createdAtEnd : null,
    ]);
  }, [filters]);

  const handleApply = () => {
    const newFilters: AssignmentFilterModalProps['filters'] = {
      jurisdictionId: jurisdiction || undefined,
      manualId: manualId || undefined,
    };
    if (createdDateRange && createdDateRange[0] && createdDateRange[1]) {
      newFilters.createdAtStart = createdDateRange[0];
      newFilters.createdAtEnd = createdDateRange[1];
    }
    onApply(newFilters);
    onClose();
  };

  return (
    <Modal
      title={t('dtbs.assignments.filters.root', 'Filter Assignments')}
      size='sm'
      actions={
        <>
          <Button
            onClick={onClose}
            color='neutral'
          >
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            variant='contained'
            onClick={handleApply}
          >
            {t('dtbs.assignments.filters.apply', 'Apply')}
          </Button>
        </>
      }
      open={open}
      onClose={onClose}
    >
      <Stack
        sx={{ mt: 4 }}
        spacing={4}
      >
        <JurisdictionFilter
          id='jurisdiction-filter'
          value={jurisdiction ? [jurisdiction] : []}
          onChange={(val: (string | null)[]) => setJurisdiction(val[0] || '')}
          placeholder={t('dtbs.assignments.filters.jurisdiction', 'Jurisdiction')}
        />

        <ManualPicker
          id='manual-picker'
          label={t('dtbs.assignments.filters.manual', 'Manual')}
          organizationId={currentOrganizationId || ''}
          value={manualId ? [manualId] : []}
          onChange={(newValue: string[]) => setManualId(newValue[0] || '')}
          placeholder={t('dtbs.assignments.filters.selectManual', 'Select Manual')}
        />
        <FormControl fullWidth>
          <FormLabel>{t('dtbs.assignments.filters.createdDate', 'Created Date')}</FormLabel>
          <DateRangePicker
            value={createdDateRange}
            onChange={(newValue) => setCreatedDateRange(newValue)}
            sx={{ '& .MuiFormControl-root': { paddingTop: 0 }, '& .MuiFormLabel-root': { display: 'none' } }}
          />
        </FormControl>
      </Stack>
    </Modal>
  );
};

export default AssignmentFilterModal;
